/**
 * Price Calculation Utilities
 *
 * Helper functions for investment pricing, gains/losses, and value calculations
 */

/**
 * Calculate current value of an investment
 *
 * @param {number} quantity - Number of shares/units held
 * @param {number} currentPrice - Current market price per unit
 * @returns {number} Total current value
 */
export const calculateCurrentValue = (quantity, currentPrice) => {
  if (!quantity || !currentPrice || isNaN(quantity) || isNaN(currentPrice)) {
    return 0;
  }
  return quantity * currentPrice;
};

/**
 * Calculate cost basis of an investment
 *
 * @param {number} quantity - Number of shares/units held
 * @param {number} averageCostBasis - Average cost per unit
 * @returns {number} Total cost basis
 */
export const calculateCostBasis = (quantity, averageCostBasis) => {
  if (!quantity || !averageCostBasis || isNaN(quantity) || isNaN(averageCostBasis)) {
    return 0;
  }
  return quantity * averageCostBasis;
};

/**
 * Calculate gain or loss
 *
 * @param {number} currentValue - Current market value
 * @param {number} costBasis - Original cost basis
 * @returns {number} Gain (positive) or loss (negative)
 */
export const calculateGainLoss = (currentValue, costBasis) => {
  if (isNaN(currentValue) || isNaN(costBasis)) {
    return 0;
  }
  return currentValue - costBasis;
};

/**
 * Calculate gain/loss percentage
 *
 * @param {number} gainLoss - Absolute gain or loss amount
 * @param {number} costBasis - Original cost basis
 * @returns {number} Percentage gain (positive) or loss (negative)
 */
export const calculateGainLossPercent = (gainLoss, costBasis) => {
  if (!costBasis || costBasis === 0 || isNaN(gainLoss) || isNaN(costBasis)) {
    return 0;
  }
  return (gainLoss / costBasis) * 100;
};

/**
 * Calculate all investment metrics at once
 *
 * @param {Object} params - { quantity, currentPrice, averageCostBasis }
 * @returns {Object} { currentValue, costBasis, gainLoss, gainLossPercent }
 */
export const calculateInvestmentMetrics = ({ quantity, currentPrice, averageCostBasis }) => {
  const currentValue = calculateCurrentValue(quantity, currentPrice);
  const costBasis = calculateCostBasis(quantity, averageCostBasis);
  const gainLoss = calculateGainLoss(currentValue, costBasis);
  const gainLossPercent = calculateGainLossPercent(gainLoss, costBasis);

  return {
    currentValue,
    costBasis,
    gainLoss,
    gainLossPercent
  };
};

/**
 * Format number as currency
 *
 * @param {number} value - Numeric value
 * @param {string} currency - Currency code (USD, GBP, EUR)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'EUR', decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const symbols = {
    USD: '$',
    GBP: '£',
    EUR: '€',
    CHF: 'CHF',
    JPY: '¥',
    CNY: '¥'
  };

  const symbol = symbols[currency] || currency;
  const absValue = Math.abs(value);

  // Format with commas for thousands
  const formattedValue = absValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  return value < 0 ? `-${symbol}${formattedValue}` : `${symbol}${formattedValue}`;
};

/**
 * Format percentage with sign
 *
 * @param {number} value - Percentage value
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

/**
 * Format large numbers with K/M/B suffix
 *
 * @param {number} value - Numeric value
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted string (e.g., "1.5K", "2.3M")
 */
export const formatCompactNumber = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e9) {
    return `${sign}${(absValue / 1e9).toFixed(decimals)}B`;
  } else if (absValue >= 1e6) {
    return `${sign}${(absValue / 1e6).toFixed(decimals)}M`;
  } else if (absValue >= 1e3) {
    return `${sign}${(absValue / 1e3).toFixed(decimals)}K`;
  }

  return `${sign}${absValue.toFixed(decimals)}`;
};

/**
 * Get color class based on gain/loss value
 *
 * @param {number} value - Gain/loss value
 * @returns {string} Tailwind color class
 */
export const getGainLossColor = (value) => {
  if (value > 0) return 'text-green-600 dark:text-green-400';
  if (value < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-400';
};

/**
 * Get arrow icon based on value direction
 *
 * @param {number} value - Value to check
 * @returns {string} Arrow character (↑, ↓, →)
 */
export const getArrowIcon = (value) => {
  if (value > 0) return '↑';
  if (value < 0) return '↓';
  return '→';
};

/**
 * Calculate portfolio allocation percentage
 *
 * @param {number} investmentValue - Value of single investment
 * @param {number} totalPortfolioValue - Total portfolio value
 * @returns {number} Percentage allocation
 */
export const calculateAllocationPercent = (investmentValue, totalPortfolioValue) => {
  if (!totalPortfolioValue || totalPortfolioValue === 0) {
    return 0;
  }
  return (investmentValue / totalPortfolioValue) * 100;
};

/**
 * Check if price data is stale (older than 24 hours)
 *
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {boolean} True if stale
 */
export const isPriceStale = (timestamp) => {
  if (!timestamp) return true;

  const ONE_DAY = 24 * 60 * 60 * 1000;
  const age = Date.now() - timestamp;

  return age > ONE_DAY;
};

/**
 * Format timestamp as relative time
 *
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} Relative time string (e.g., "2 hours ago")
 */
export const formatLastUpdated = (timestamp) => {
  if (!timestamp) return 'Never';

  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;

  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
};

/**
 * Format date as readable string
 *
 * @param {string|number|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '-';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Convert currency using exchange rates
 *
 * @param {number} amount - Amount in source currency
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @param {Object} exchangeRates - Exchange rates object
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates) => {
  if (!amount || !exchangeRates || !exchangeRates.rates) {
    return amount;
  }

  if (fromCurrency === toCurrency) {
    return amount;
  }

  const fromRate = exchangeRates.rates[fromCurrency] || 1;
  const toRate = exchangeRates.rates[toCurrency] || 1;

  // Convert to base currency (EUR) then to target currency
  return (amount / fromRate) * toRate;
};

/**
 * Validate numeric input
 *
 * @param {any} value - Value to validate
 * @returns {boolean} True if valid number
 */
export const isValidNumber = (value) => {
  return value !== null && value !== undefined && !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Safe number parsing
 *
 * @param {any} value - Value to parse
 * @param {number} defaultValue - Default value if parsing fails
 * @returns {number} Parsed number or default
 */
export const safeParseNumber = (value, defaultValue = 0) => {
  const parsed = parseFloat(value);
  return isValidNumber(parsed) ? parsed : defaultValue;
};
